import { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../ui/select";
import { Slider } from "../ui/slider";
import {
  Loader2, Play, Clock, TrendingDown, AlertTriangle, Mail, Bell,
  Calendar, History, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { formatCurrency } from "../../lib/utils";
import { VarianceAlertRow } from "./VarianceAlertRow";

const WEEKDAYS = [
  { v: 0, label: "Lunes" }, { v: 1, label: "Martes" }, { v: 2, label: "Miércoles" },
  { v: 3, label: "Jueves" }, { v: 4, label: "Viernes" }, { v: 5, label: "Sábado" },
  { v: 6, label: "Domingo" },
];

const TIMEZONES = [
  "Europe/Madrid", "Europe/London", "Europe/Paris", "Europe/Berlin",
  "Europe/Rome", "Europe/Lisbon", "America/Mexico_City", "America/Argentina/Buenos_Aires",
  "America/Bogota", "America/Santiago", "America/New_York", "Atlantic/Canary",
];

/**
 * Alertas de merma oculta — modal de configuración + ejecución manual.
 *
 * Backend:
 *   GET    /restaurants/{rid}/variance-alerts/config
 *   PATCH  /restaurants/{rid}/variance-alerts/config
 *   POST   /restaurants/{rid}/variance-alerts/run-now?send_email=true
 *   GET    /restaurants/{rid}/variance-alerts/history
 */
export function VarianceAlertSettings({ open, onOpenChange, restaurantId }) {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [lastReport, setLastReport] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (!open || !restaurantId) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.getVarianceAlertConfig(restaurantId),
      api.getVarianceAlertHistory(restaurantId, 10),
    ])
      .then(([c, h]) => {
        if (cancelled) return;
        setCfg(c);
        setHistory(h?.items || []);
        setLastReport(h?.items?.[0] || null);
      })
      .catch(() => {
        if (!cancelled) toast.error("No se pudo cargar la configuración");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, restaurantId]);

  const patch = async (next) => {
    setCfg(prev => ({ ...prev, ...next }));
    setSaving(true);
    try {
      const updated = await api.updateVarianceAlertConfig(restaurantId, next);
      setCfg(updated);
    } catch (err) {
      toast.error(err?.message || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleRunNow = async () => {
    if (!restaurantId || running) return;
    setRunning(true);
    try {
      const sendEmail = (cfg?.channels || []).includes("email");
      const r = await api.runVarianceAlertNow(restaurantId, {
        sendEmail,
        thresholdPct: cfg?.threshold_pct,
      });
      setLastReport(r);
      // Refresh history
      const h = await api.getVarianceAlertHistory(restaurantId, 10);
      setHistory(h?.items || []);
      if (r.alerts_count > 0) {
        toast.warning(`${r.alerts_count} producto${r.alerts_count > 1 ? "s" : ""} con merma oculta`, {
          description: `Pérdida estimada: ${formatCurrency(r.total_loss_value)}`,
        });
      } else {
        toast.success("Sin mermas ocultas detectadas — ¡buen trabajo!");
      }
    } catch (err) {
      toast.error(err?.message || "Error al ejecutar la comprobación");
    } finally {
      setRunning(false);
    }
  };

  const toggleChannel = (channel) => {
    const current = new Set(cfg?.channels || ["in_app"]);
    if (current.has(channel)) current.delete(channel);
    else current.add(channel);
    if (current.size === 0) current.add("in_app");
    patch({ channels: [...current] });
  };

  const nextRunLabel = useMemo(() => {
    if (!cfg?.enabled || cfg?.frequency === "off") return "Desactivada";
    const h = String(cfg?.hour_local ?? 22).padStart(2, "0");
    const m = String(cfg?.minute_local ?? 0).padStart(2, "0");
    const tz = cfg?.timezone || "Europe/Madrid";
    if (cfg.frequency === "weekly") {
      const day = WEEKDAYS.find(d => d.v === cfg.weekday)?.label || "Lunes";
      return `Cada ${day.toLowerCase()} a las ${h}:${m} (${tz})`;
    }
    return `Todos los días a las ${h}:${m} (${tz})`;
  }, [cfg]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="variance-alert-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingDown className="w-5 h-5 text-red-500" />
            Alertas de merma oculta
          </DialogTitle>
          <DialogDescription>
            Detecta automáticamente cuando el stock real difiere del teórico (ventas, recuentos, compras) por encima de un umbral.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Enable toggle */}
            <div className="flex items-center justify-between p-4 rounded-lg border border-border/60 bg-muted/20">
              <div>
                <Label htmlFor="variance-enabled" className="text-base font-semibold">
                  Alertas automáticas
                </Label>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {cfg?.enabled ? nextRunLabel : "Actualmente desactivadas"}
                </p>
              </div>
              <Switch
                id="variance-enabled"
                checked={!!cfg?.enabled}
                onCheckedChange={(v) => patch({ enabled: !!v })}
                data-testid="variance-enabled-switch"
              />
            </div>

            {/* Schedule */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold flex items-center gap-2">
                <Clock className="w-4 h-4" /> Cuándo ejecutar
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Frecuencia</Label>
                  <Select
                    value={cfg?.frequency || "daily"}
                    onValueChange={(v) => patch({ frequency: v })}
                  >
                    <SelectTrigger data-testid="variance-frequency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Diaria</SelectItem>
                      <SelectItem value="weekly">Semanal</SelectItem>
                      <SelectItem value="off">Desactivada</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {cfg?.frequency === "weekly" && (
                  <div className="space-y-1.5">
                    <Label className="text-xs">Día de la semana</Label>
                    <Select
                      value={String(cfg?.weekday ?? 0)}
                      onValueChange={(v) => patch({ weekday: parseInt(v, 10) })}
                    >
                      <SelectTrigger data-testid="variance-weekday-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {WEEKDAYS.map(d => (
                          <SelectItem key={d.v} value={String(d.v)}>{d.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label className="text-xs">Hora (HH:MM)</Label>
                  <Input
                    type="time"
                    value={`${String(cfg?.hour_local ?? 22).padStart(2, "0")}:${String(cfg?.minute_local ?? 0).padStart(2, "0")}`}
                    onChange={(e) => {
                      const [h, m] = e.target.value.split(":");
                      patch({ hour_local: parseInt(h, 10), minute_local: parseInt(m, 10) });
                    }}
                    data-testid="variance-time-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Zona horaria</Label>
                  <Select
                    value={cfg?.timezone || "Europe/Madrid"}
                    onValueChange={(v) => patch({ timezone: v })}
                  >
                    <SelectTrigger data-testid="variance-timezone-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMEZONES.map(tz => (
                        <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Threshold */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" /> Umbral de varianza
                </Label>
                <Badge variant="outline" className="text-sm font-mono" data-testid="variance-threshold-badge">
                  {cfg?.threshold_pct ?? 15}%
                </Badge>
              </div>
              <Slider
                value={[cfg?.threshold_pct ?? 15]}
                min={5} max={50} step={1}
                onValueChange={(v) => setCfg(prev => ({ ...prev, threshold_pct: v[0] }))}
                onValueCommit={(v) => patch({ threshold_pct: v[0] })}
                data-testid="variance-threshold-slider"
              />
              <p className="text-xs text-muted-foreground">
                Solo se notificará cuando la diferencia entre teórico y real supere este porcentaje.
              </p>
            </div>

            {/* Channels */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Canales</Label>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button" size="sm"
                  variant={(cfg?.channels || []).includes("in_app") ? "default" : "outline"}
                  onClick={() => toggleChannel("in_app")}
                  className="gap-1.5"
                  data-testid="variance-channel-in-app"
                >
                  <Bell className="w-3.5 h-3.5" /> En la app
                </Button>
                <Button
                  type="button" size="sm"
                  variant={(cfg?.channels || []).includes("email") ? "default" : "outline"}
                  onClick={() => toggleChannel("email")}
                  className="gap-1.5"
                  data-testid="variance-channel-email"
                >
                  <Mail className="w-3.5 h-3.5" /> Email
                </Button>
              </div>
            </div>

            {/* Last report / history */}
            {(lastReport || history.length > 0) && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold flex items-center gap-2">
                  <History className="w-4 h-4" /> Última ejecución
                </h4>
                {lastReport ? (
                  <div className="p-3 rounded-lg border border-border/60 bg-muted/10" data-testid="variance-last-report">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <p className="text-sm font-medium">
                          {lastReport.alerts_count > 0
                            ? `${lastReport.alerts_count} producto${lastReport.alerts_count > 1 ? "s" : ""} con merma oculta`
                            : "Sin mermas ocultas detectadas"}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {lastReport.generated_at ? new Date(lastReport.generated_at).toLocaleString("es-ES") : "—"}
                          {lastReport.trigger && ` · ${lastReport.trigger === "manual" ? "manual" : "automática"}`}
                        </p>
                      </div>
                      {lastReport.alerts_count > 0 && (
                        <Badge className="bg-red-100 text-red-700 border-red-200 hover:bg-red-100">
                          {formatCurrency(lastReport.total_loss_value)} perdidos
                        </Badge>
                      )}
                    </div>
                    {lastReport.alerts?.length > 0 && (
                      <div className="mt-3 space-y-1">
                        {lastReport.alerts.slice(0, 5).map((a, i) => (
                          <VarianceAlertRow
                            key={i}
                            alert={a}
                            restaurantId={restaurantId}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground italic">Aún no se ha ejecutado ninguna comprobación.</p>
                )}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          {saving && (
            <span className="text-xs text-muted-foreground mr-auto flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" /> Guardando…
            </span>
          )}
          <Button
            onClick={handleRunNow}
            disabled={running || !restaurantId}
            className="gap-1.5"
            data-testid="variance-run-now-btn"
          >
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {running ? "Comprobando…" : "Ejecutar ahora"}
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="variance-close-btn">
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
